import React from 'react';
import { formatDate } from '../utils/dateUtils';
import { formatSimpleCurrency } from '../utils/currencyUtils';
import { printSaleInvoice, createSaleInvoiceData } from '../utils/invoiceUtils';
import { BrandLogo } from './BrandLogo';

interface SaleDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  record: any | null; // Sale record
  carInfo: {
    brandId: string;
    brand: string;
    model: string;
    plate?: string;
    year?: number;
    color?: string;
  } | null;
}

export const SaleDetailModal: React.FC<SaleDetailModalProps> = ({
  isOpen,
  onClose,
  record,
  carInfo
}) => {
  if (!isOpen || !record || !carInfo) return null;

  const handlePrintInvoice = () => {
    const invoiceData = createSaleInvoiceData(record, carInfo);
    printSaleInvoice(invoiceData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4 text-white rounded-t-2xl">
          <h2 className="text-xl font-semibold">💰 Sale Transaction Details</h2>
          <p className="text-purple-100 text-sm mt-1">
            Complete information about this car sale
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Car Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9L18 10c-.1-.1-.4-.6-1.4-1.3C16.1 8.4 14.7 8 13 8H9.5c-.6 0-1.1.4-1.4.9L6.8 11H4c-.6 0-1 .4-1 1v3c0 .6.4 1 1 1h2c0 1.7 1.3 3 3 3s3-1.3 3-3h4c0 1.7 1.3 3 3 3s3-1.3 3-3z"/>
              </svg>
              Vehicle Information
            </h3>
            <div className="flex items-center space-x-4">
              <BrandLogo brandId={carInfo.brandId} size="lg" />
              <div>
                <h4 className="font-semibold text-gray-900 text-lg">
                  {carInfo.brand} {carInfo.model}
                </h4>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  {carInfo.plate && <span>Plate: {carInfo.plate}</span>}
                  {carInfo.year && <span>Year: {carInfo.year}</span>}
                  {carInfo.color && <span className="capitalize">Color: {carInfo.color}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <h3 className="font-medium text-purple-900 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Customer Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-purple-700 font-medium">Name:</span>
                <span className="ml-2 text-purple-800">{record.customerName || 'N/A'}</span>
              </div>
              {record.customerPhone && (
                <div>
                  <span className="text-purple-700 font-medium">Phone:</span>
                  <span className="ml-2 text-purple-800">{record.customerPhone}</span>
                </div>
              )}
              {record.customerLicense && (
                <div>
                  <span className="text-purple-700 font-medium">License:</span>
                  <span className="ml-2 text-purple-800">{record.customerLicense}</span>
                </div>
              )}
            </div>
          </div>

          {/* Sale Information */}
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h3 className="font-medium text-green-900 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
              Sale Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-green-700 font-medium">Sale Date:</span>
                <span className="ml-2 text-green-800">{formatDate(record.saleDate)}</span>
              </div>
              <div>
                <span className="text-green-700 font-medium">Final Price:</span>
                <span className="ml-2 text-green-800 font-semibold">{formatSimpleCurrency(record.salePrice, true)}</span>
              </div>
              {record.originalPrice && record.originalPrice !== record.salePrice && (
                <>
                  <div>
                    <span className="text-green-700 font-medium">Listed Price:</span>
                    <span className="ml-2 text-green-800">{formatSimpleCurrency(record.originalPrice, true)}</span>
                  </div>
                  <div>
                    <span className="text-green-700 font-medium">Difference:</span>
                    <span className={`ml-2 font-semibold ${
                      record.salePrice >= record.originalPrice ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {record.salePrice >= record.originalPrice ? '+' : ''}
                      {formatSimpleCurrency(record.salePrice - record.originalPrice, true)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Transaction Summary */}
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
            <h3 className="font-medium text-purple-900 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Transaction Summary
            </h3>
            <div className="flex items-center justify-between">
              <span className="text-lg font-medium text-purple-700">Total Sale Amount:</span>
              <span className="text-2xl font-bold text-purple-900">{formatSimpleCurrency(record.salePrice, true)}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-t rounded-b-2xl">
          <button
            onClick={handlePrintInvoice}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            <span>Print Invoice</span>
          </button>
          
          <button
            onClick={onClose}
            className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
